import { eventBus, EVENTS } from '../../../utils/EventBus'
import React, { useRef, useCallback, useState, useEffect } from 'react'
import { getKeyboardNavigationDirection } from './DistributionTable/utils/keyboardNavigation'

const EditableFixedCell = ({
  item,
  field,
  value,
  onChange,
  type = 'text',
  distributionManager,
  inputClassName,
  inputStyle,
  rowSpan,
  maxLength,
  numericOnly = true,
  validateValue,
  normalizeOnCommit,
  readOnly = false
}) => {
  const inputRef = useRef(null)
  const isCommittingRef = useRef(false)
  const isDirtyRef = useRef(false)
  const controlKeyRef = useRef({ pending: false, combined: false })

  const getDisplayValue = useCallback(() => {
    if (distributionManager && item.hinmeiCode && item.niukabango) {
      const fixedValue = distributionManager.getFixedField(
        item.hinmeiCode,
        item.niukabango,
        field,
        value
      )

      return fixedValue ?? value ?? ''
    }
    return value ?? ''
  }, [
    distributionManager,
    item.hinmeiCode,
    item.niukabango,
    field,
    value
  ])

  const [localValue, setLocalValue] = useState(() => getDisplayValue())

  useEffect(() => {
    if (isCommittingRef.current) return

    const displayValue = getDisplayValue()
    setLocalValue(displayValue)
  }, [value, getDisplayValue])

  useEffect(() => {
    const unsubscribe = eventBus.on(EVENTS.DISTRIBUTION_CHANGED, (data) => {
      if (
        data.hinmeiCode === item.hinmeiCode &&
        String(data.niukabango) === String(item.niukabango) &&
        data.field === field &&
        data.type === 'fixed'
      ) {
        setLocalValue(getDisplayValue())
      }
    })
    return unsubscribe
  }, [item.hinmeiCode, item.niukabango, field, getDisplayValue])

  const handleChange = useCallback(
    (e) => {
      if (readOnly) return
      const newValue = e.target.value
      if (numericOnly && !/^\d*$/.test(newValue)) return
      if (maxLength && newValue.length > maxLength) return
      if (validateValue && !validateValue(newValue)) return
      setLocalValue(newValue)
      isDirtyRef.current = true
    },
    [maxLength, numericOnly, validateValue, readOnly]
  )

  const commitValue = useCallback(
    (newValue) => {
      if (readOnly) return true
      if (!isDirtyRef.current) return true
      if (validateValue && !validateValue(newValue)) {
        return false
      }

      const commitValue = normalizeOnCommit
        ? normalizeOnCommit(newValue)
        : newValue
      isCommittingRef.current = true
      isDirtyRef.current = false
      setLocalValue(commitValue)
      onChange(item, field, commitValue)
      setTimeout(() => {
        isCommittingRef.current = false
      }, 50)
      return true
    },
    [
      item,
      field,
      onChange,
      normalizeOnCommit,
      validateValue,
      readOnly
    ]
  )

  const handleBlur = useCallback(
    (e) => {
      controlKeyRef.current = { pending: false, combined: false }
      commitValue(e.target.value)
    },
    [commitValue]
  )

  const handleFocus = useCallback(
    (e) => {
      if (readOnly) return
      e.target.select()
      eventBus.emit(EVENTS.CELL_FOCUSED, {
        hinmeiCode: item.hinmeiCode,
        niukabango: item.niukabango,
        field
      })
    },
    [item.hinmeiCode, item.niukabango, field, readOnly]
  )

  const ensureVisible = useCallback((element) => {
    const scrollContainer = element.closest('.rp-table-scroll')
    if (!scrollContainer) return

    const rect = element.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()

    const stickyCols = Array.from(
      scrollContainer.querySelectorAll('.sticky-col')
    )
    let stickyWidth = 0
    if (stickyCols.length > 0) {
      const lastSticky = stickyCols[stickyCols.length - 1]
      const lastStickyRect = lastSticky.getBoundingClientRect()
      stickyWidth = lastStickyRect.right - containerRect.left
    }

    if (rect.left < containerRect.left + stickyWidth) {
      scrollContainer.scrollLeft -=
        containerRect.left + stickyWidth - rect.left + 20
    } else if (rect.right > containerRect.right) {
      scrollContainer.scrollLeft += rect.right - containerRect.right + 20
    }

    const thead = scrollContainer.querySelector('thead')
    const headerHeight = thead ? thead.getBoundingClientRect().height : 0

    if (rect.top < containerRect.top + headerHeight) {
      scrollContainer.scrollTop -= (containerRect.top + headerHeight - rect.top + 20)
    } else if (rect.bottom > containerRect.bottom) {
      scrollContainer.scrollTop += (rect.bottom - containerRect.bottom + 20)
    }
  }, [])

  const findNextInput = useCallback((currentInput, direction) => {
    const currentTd = currentInput.closest('td, th')
    if (!currentTd) return null
    const currentTr = currentTd.closest('tr')
    if (!currentTr) return null

    const allTrs = Array.from(document.querySelectorAll('.rp-table tr'))
    const currentTrIdx = allTrs.indexOf(currentTr)

    const isAutocomplete = (el) => el.closest('.MuiAutocomplete-root')

    if (direction === 'up' || direction === 'down') {
      const step = direction === 'up' ? -1 : 1
      const currentRect = currentTd.getBoundingClientRect()
      const currentCenter = currentRect.left + currentRect.width / 2

      for (
        let i = currentTrIdx + step;
        i >= 0 && i < allTrs.length;
        i += step
      ) {
        const row = allTrs[i]
        const cells = Array.from(row.cells)

        const targetTd = cells.find((td) => {
          const rect = td.getBoundingClientRect()
          return currentCenter >= rect.left && currentCenter <= rect.right
        })

        if (targetTd) {
          const input = targetTd.querySelector('input')
          if (
            input &&
            !input.disabled &&
            !input.readOnly &&
            !isAutocomplete(input)
          ) {
            return input
          }
        }
      }
    } else if (direction === 'left' || direction === 'right') {
      const allInputs = Array.from(
        document.querySelectorAll('.rp-table input')
      ).filter((i) => !i.disabled && !i.readOnly && !isAutocomplete(i))

      const currentIndex = allInputs.indexOf(currentInput)
      if (direction === 'left' && currentIndex > 0)
        return allInputs[currentIndex - 1]
      if (direction === 'right' && currentIndex < allInputs.length - 1)
        return allInputs[currentIndex + 1]
    }
    return null
  }, [])

  const handleKeyDown = useCallback(
    (e) => {
      if (readOnly) return
      const { key, target } = e
      if (key === 'Control') {
        controlKeyRef.current = { pending: true, combined: false }
        return
      }
      if (e.ctrlKey || controlKeyRef.current.pending) {
        if (controlKeyRef.current.pending) {
          controlKeyRef.current.combined = true
        }
        return
      }
      const keyboardDirection = getKeyboardNavigationDirection(key)
      if (keyboardDirection) {
        e.preventDefault()
        if (!commitValue(target.value)) return

        const nextInput = findNextInput(target, keyboardDirection)
        if (nextInput) {
          nextInput.focus()
          nextInput.select()
          ensureVisible(nextInput)
        }
      } else if (key === 'Escape') {
        e.preventDefault()
        isDirtyRef.current = false
        setLocalValue(getDisplayValue())
        target.blur()
      } else if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)
      ) {
        const isLeft = key === 'ArrowLeft'
        const isRight = key === 'ArrowRight'
        const isUp = key === 'ArrowUp'
        const isDown = key === 'ArrowDown'

        if (isLeft && target.selectionStart !== 0) return
        if (isRight && target.selectionEnd !== target.value.length) return

        const direction = isUp
          ? 'up'
          : isDown
            ? 'down'
            : isLeft
              ? 'left'
              : 'right'
        const nextInput = findNextInput(target, direction)

        if (nextInput) {
          e.preventDefault()
          nextInput.focus()
          nextInput.select()
          ensureVisible(nextInput)
        }
      }
    },
    [
      commitValue,
      getDisplayValue,
      findNextInput,
      ensureVisible,
      readOnly
    ]
  )

  const handleKeyUp = useCallback(
    (e) => {
      if (e.key !== 'Control') return
      const controlState = controlKeyRef.current
      controlKeyRef.current = { pending: false, combined: false }
      if (readOnly || !controlState.pending || controlState.combined) return

      e.preventDefault()
      if (!commitValue(e.currentTarget.value)) return

      const nextInput = findNextInput(e.currentTarget, 'left')
      if (nextInput) {
        nextInput.focus()
        nextInput.select()
        ensureVisible(nextInput)
      }
    },
    [commitValue, ensureVisible, findNextInput, readOnly]
  )

  return (
    <td
      className={`data-cell editable-fixed-cell ${inputClassName ? inputClassName : ''}`}
      style={{ ...inputStyle }}
      rowSpan={rowSpan}
    >
      <input
        ref={inputRef}
        type={type}
        className='rp-cell-input text-[12px] py-2'
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        title={readOnly ? '' : 'クリックして編集'}
        maxLength={maxLength}
        readOnly={readOnly}
      />
    </td>
  )
}

export default React.memo(EditableFixedCell)
